import { useState } from 'react';
import { Button } from '@/shared/ui/Button';
import type { IntegrationStepData } from '../api/onboardingApi';
import styles from './StepForm.module.css';
import integrationStyles from './IntegrationsStep.module.css';

const INTEGRATIONS = [
  {
    key: 'googleSearchConsole' as const,
    name: 'Google Search Console',
    description: 'Track your search performance and see which queries bring visitors to your site.',
    icon: 'G',
    iconColor: '#4285F4',
    recommended: true,
  },
  {
    key: 'googleAnalytics' as const,
    name: 'Google Analytics 4',
    description: 'Understand user behavior, traffic sources, and conversions on your website.',
    icon: 'GA',
    iconColor: '#E37400',
    recommended: true,
  },
  {
    key: 'googleAds' as const,
    name: 'Google Ads',
    description: 'Monitor your Google Ads campaigns, spending, and return on ad spend.',
    icon: 'Ads',
    iconColor: '#34A853',
    recommended: false,
  },
  {
    key: 'metaAds' as const,
    name: 'Meta Ads',
    description: 'Track Facebook and Instagram ad performance and audience insights.',
    icon: 'M',
    iconColor: '#1877F2',
    recommended: false,
  },
];

interface IntegrationsStepProps {
  onSubmit: (data: IntegrationStepData) => void;
  onBack: () => void;
  isLoading: boolean;
}

export function IntegrationsStep({ onSubmit, onBack, isLoading }: IntegrationsStepProps) {
  const [selected, setSelected] = useState<IntegrationStepData>({
    googleSearchConsole: false,
    googleAnalytics: false,
    googleAds: false,
    metaAds: false,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(selected);
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div>
        <p className={styles.sectionTitle}>Connect your marketing tools</p>
        <p className={integrationStyles.hint}>
          You can skip this step and connect integrations later from the Settings page.
        </p>
        <div className={integrationStyles.cards}>
          {INTEGRATIONS.map((integration) => (
            <label
              key={integration.key}
              className={`${integrationStyles.card} ${selected[integration.key] ? integrationStyles.selected : ''}`}
            >
              <div className={integrationStyles.cardHeader}>
                <div
                  className={integrationStyles.icon}
                  style={{ backgroundColor: integration.iconColor + '20', color: integration.iconColor }}
                >
                  {integration.icon}
                </div>
                <div className={integrationStyles.cardInfo}>
                  <div className={integrationStyles.name}>
                    {integration.name}
                    {integration.recommended && (
                      <span className={integrationStyles.badge}>Recommended</span>
                    )}
                  </div>
                  <div className={integrationStyles.description}>{integration.description}</div>
                </div>
                <input
                  type="checkbox"
                  checked={selected[integration.key] ?? false}
                  onChange={(e) => setSelected((prev) => ({ ...prev, [integration.key]: e.target.checked }))}
                  className={integrationStyles.checkbox}
                />
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className={styles.actions}>
        <Button type="button" variant="secondary" size="lg" onClick={onBack}>
          Back
        </Button>
        <Button type="submit" isLoading={isLoading} size="lg">
          Continue
        </Button>
      </div>
    </form>
  );
}
